'use client';

import { useLayoutEffect, useMemo, useRef, type MouseEvent } from 'react';

import { Button } from '@/app/Components/Button';

export type ScheduleGenerationStatus = 'idle' | 'scheduled' | 'running' | 'stopped' | 'timeout' | 'error' | 'completed';

export interface ScheduleGenerationFeedbackProps {
  status: ScheduleGenerationStatus;
  onStop: () => void;
  error?: string | null;
  previousLabel?: string | null;
  className?: string;
  /** Committed owner/workspace/input identity; each rendered instance owns its focus. */
  focusContextKey?: string;
}

/** Generation lifecycle only; feasibility and result selection belong to the workspace. */
export default function ScheduleGenerationFeedback({ status, onStop, error, previousLabel, className = '', focusContextKey = '' }: ScheduleGenerationFeedbackProps) {
  const regionRef = useRef<HTMLDivElement | null>(null);
  const context = useMemo(() => ({ focusContextKey }), [focusContextKey]);
  const committedContext = useRef<typeof context | null>(null);
  const focusRequest = useRef<{ context: typeof context; button: HTMLButtonElement } | null>(null);
  useLayoutEffect(() => {
    committedContext.current = context;
    const retireExternalFocus = (event: FocusEvent) => {
      if (focusRequest.current && event.target !== focusRequest.current.button) focusRequest.current = null;
    };
    document.addEventListener('focusin', retireExternalFocus, true);
    return () => {
      if (committedContext.current === context) committedContext.current = null;
      focusRequest.current = null;
      document.removeEventListener('focusin', retireExternalFocus, true);
    };
  }, [context]);
  useLayoutEffect(() => {
    // Consume only the next committed result of this action. A restart or any
    // intervening render retires it rather than moving focus on a later stop.
    const requested = focusRequest.current;
    focusRequest.current = null;
    if (!requested || requested.context !== context || committedContext.current !== context || status !== 'stopped') return;
    if (document.activeElement === document.body || document.activeElement === requested.button) {
      regionRef.current?.focus({ preventScroll: true });
    }
  });
  const stopWithFocusHandoff = (event: MouseEvent<HTMLButtonElement>) => {
    if (committedContext.current !== context) return;
    focusRequest.current = document.activeElement === event.currentTarget ? { context, button: event.currentTarget } : null;
    try { onStop(); } catch (reason) { focusRequest.current = null; throw reason; }
  };
  const previous = previousLabel?.trim();
  const active = status === 'scheduled' || status === 'running';
  const message = status === 'running' ? 'Generating schedules…'
    : status === 'scheduled' ? 'Updating schedules…'
      : status === 'stopped' ? 'Generation stopped.'
        : status === 'timeout' ? 'Generation took too long. Try fewer courses.'
          : status === 'error' ? error?.trim() || 'Schedules could not be generated. Try again.'
            : null;

  if (!message && !previous) return null;

  return <div ref={regionRef} tabIndex={-1} role="status" aria-live="polite" aria-atomic="true" className={`flex flex-wrap items-center justify-between gap-3 rounded-control border border-line bg-surface-subtle px-3 py-2 text-sm text-content ${className}`}>
    <div className="min-w-0 flex-1 space-y-1 break-words">
      {message && <p>{message}</p>}
      {previous && <p className="text-content-muted">{previous}</p>}
    </div>
    {active && <Button type="button" variant="secondary" className="shrink-0" onClick={stopWithFocusHandoff}>Stop</Button>}
  </div>;
}
